import { useState } from 'react';
import type { GetStaticPropsContext } from 'next';

import { fetchBlogAuthorPaths } from '#/lib/sanity/fetchers/fetchBlogAuthorPaths';
import {
  fetchBlogAuthorProps,
  type BlogAuthorProps,
} from '#/lib/sanity/fetchers/fetchBlogAuthorProps';
import { BasicText } from '#/components/BasicText';
import { Button } from '#/components/Button';
import { Headline } from '#/components/Headline';
import { Link } from '#/components/Link';
import { Media } from '#/components/Media';

export const config = {
  maxDuration: 30,
};

export async function getStaticPaths() {
  const paths = await fetchBlogAuthorPaths();

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string }>) {
  const props = await fetchBlogAuthorProps(params!.slug, draftMode);

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props };
}

export default function BlogAuthor(props: BlogAuthorProps) {
  const [postCount, setPostCount] = useState(6);

  return (
    <div className="p-main mb-50 pt-16 m:mb-100 m:pt-48">
      <div className="mx-auto max-w-980">
        <div className="mb-50 m:mb-60 m:px-30">
          <div className="flex gap-x-25 m:gap-x-50">
            <Media
              media={props.page.avatar}
              className="size-85 rounded-full m:size-170"
              mediaClassName="object-cover"
              sizes={['85px', { m: '170px' }]}
              placeholder={true}
              aspect={false}
            />
            <div className="pt-15 m:pt-8">
              <Headline as={'h1'} value={props.page.name} variant={5} />
              {props.page.role ? (
                <p className="mt-8 text-20 font-[440] leading-116 -tracking-1">
                  {props.page.role}
                </p>
              ) : null}
              <BasicText
                value={props.page.bio}
                className="mt-12 hidden max-w-608 m:block"
              />
              {props.page.social && props.page.social.length > 0 ? (
                <div className="mt-25 hidden gap-x-15 m:flex">
                  {props.page.social.map((item) => (
                    <Button
                      key={item._key}
                      href={item.href}
                      target={item.target ?? undefined}
                    >
                      {item.title}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="mt-15 m:hidden">
            <BasicText value={props.page.bio} className="max-w-608" />
            {props.page.social && props.page.social.length > 0 ? (
              <div className="mt-20 flex gap-x-15">
                {props.page.social.map((item) => (
                  <Button
                    key={item._key}
                    href={item.href}
                    target={item.target ?? undefined}
                  >
                    {item.title}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="-mx-10 flex flex-wrap gap-y-40">
          {props.page.posts.slice(0, postCount).map((post) => (
            <Link
              href={post.href}
              key={post._key}
              className="block px-10 600:w-1/2"
            >
              <Media
                media={post.thumbnailImage}
                className="aspect-[16/9] rounded-16"
                mediaClassName="object-cover"
                sizes={['100vw', { '600px': '50vw', l: '490px' }]}
                placeholder={true}
                aspect={false}
              />
              <Headline
                as={'h2'}
                value={post.title}
                variant={6}
                className="mt-15 !leading-116 m:mt-20"
              />
              <p className="mt-8 text-grayscale-06 m:mt-10">{post.excerpt}</p>
              <p className="mt-10 flex text-12 text-grayscale-05 m:mt-15 l:text-14">
                {new Date(post.publishedAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
                {post.readTime ? (
                  <>
                    <span className="px-7">•</span>
                    {post.readTime} min read
                  </>
                ) : null}
              </p>
            </Link>
          ))}
        </div>
        {props.page.posts.length > postCount ? (
          <div className="mt-50 text-center m:mt-60">
            <Button onClick={() => setPostCount(postCount + 6)}>
              View more
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
